import React, { useState, useRef, useEffect } from "react";
import { Check, X, Info, Download } from "lucide-react";
import { API_URLS } from "../api/urls";
import { AuthStorage } from "./auth/AuthStorage";
import { ReactComponent as GiftBox } from "../../assets/icons/svg/gift-box.svg";
import PartyConfetti from "../../assets/icons/gif/Party Confetti.gif";
import { ReactComponent as Logo } from "../../assets/icons/svg/triva_logo.svg";
import { ReactComponent as SuccessIcon } from "../../assets/icons/svg/Signature_Plan_Success_icon.svg";
import { ReactComponent as AvailableOn } from "../../assets/icons/svg/Signature_Plan_Success_available on-image.svg";
import { ReactComponent as NameBorderIcon } from "../../assets/icons/svg/plan_name_border.svg";
import { useToast } from "../ui/Toastcontext";

// Updated interface to include the new API response data
interface PaymentSuccessPageDesktopProps {
  planType?: string;
  duration?: number;
  paymentDetails?: {
    orderId: string;
    paymentId: string;
    amount: number;
    transactionDate: string;
    // New fields from UPDATE_PAYMENT_STATUS API response
    apiPaymentId?: string;
    signatureId?: string;
    invoiceNumber?: string;
    userPaymentId?: number; // This is the key field we need
  };
  onGetInvoice?: () => void;
  onBackToPlans?: () => void;
}

interface UserInfo {
  user_fname: string;
  user_lname: string;
  user_mobileno: string;
  email: string;
  profile: string;
}

interface GetUserInfoResponse {
  success: string;
  message: string;
  user_info: UserInfo;
}

interface UpdateUserInfoResponse {
  success: string;
  message: string;
}

const PaymentSuccessPageDesktop: React.FC<PaymentSuccessPageDesktopProps> = ({
  planType,
  duration,
  paymentDetails,
  onGetInvoice,
  onBackToPlans,
}) => {
  const { showToast } = useToast();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Auto logout after 30 seconds
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to format duration
  const formatDuration = (duration: number): string => {
    // If duration is less than 1, treat it as days (0.5 = 15 days)
    if (duration < 1) {
      const days = Math.round(duration * 30); // Convert fraction of month to days
      return `${days} day${days > 1 ? "s" : ""}`;
    }
    // Otherwise treat as months
    return `${duration} month${duration > 1 ? "s" : ""}`;
  };

  useEffect(() => {
    console.log(
      "PaymentSuccessPageDesktop mounted - starting 30s logout timer"
    );

    // Start 30-second logout timer
    logoutTimerRef.current = setTimeout(() => {
      console.log("30 seconds elapsed - clearing auth and redirecting");

      // Clear authentication
      AuthStorage.clearAuth();

      // Clear any stored payment info
      localStorage.removeItem("lastPaymentSuccess");

      // Redirect using callback if provided, otherwise fallback
      if (onBackToPlans) {
        onBackToPlans();
      } else {
        window.location.href = "/";
      }
    }, 30000); // 30 seconds

    // Cleanup timer on unmount
    return () => {
      if (logoutTimerRef.current) {
        console.log("Cleaning up logout timer");
        clearTimeout(logoutTimerRef.current);
      }
    };
  }, [onBackToPlans]);

  // Prevent back navigation
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      console.log("Back navigation blocked");
      window.history.pushState(null, "", window.location.href);
    };

    // Push initial state
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Get current user ID from AuthStorage
  const getUserId = () => {
    return AuthStorage.getUserId();
  };

  // Email validation helper function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.trim() !== "" && emailRegex.test(email.trim());
  };

  // Get plan name for display
  const getPlanDisplayName = () => {
    // Use planType prop to determine plan name
    if (planType) {
      const planNames: Record<string, string> = {
        lite: "Triva Lite",
        spark: "Triva Spark",
        ignite: "Triva Ignite",
        thrive: "Triva Thrive",
        premium: "Triva Signature",
        signature: "Triva Signature",
      };

      return planNames[planType] || "Triva Plan";
    }

    return "Triva Plan";
  };

  // Download backend-generated invoice
  const handleDownloadInvoice = async () => {
    const userId = getUserId();

    if (!userId) {
      const errorMsg = "User ID not found. Please log in again.";
      setError(errorMsg);
      showToast('error', errorMsg);
      return;
    }

    if (!paymentDetails?.userPaymentId) {
      const errorMsg = "Payment ID is missing. Unable to download invoice.";
      setError(errorMsg);
      showToast('error', errorMsg);
      return;
    }

    setIsDownloading(true);
    setError("");

    try {
      console.log("=== DOWNLOADING BACKEND INVOICE (DESKTOP) ===");
      console.log("📋 User Payment ID:", paymentDetails.userPaymentId);
      
      // Get the download URL using the user_payment_id
      const downloadUrl = API_URLS.GET_INVOICE(paymentDetails.userPaymentId);
      
      console.log("🚀 Download URL:", downloadUrl);
      console.log("⏰ Download Request Timestamp:", new Date().toISOString());

      // Fetch the invoice PDF
      const response = await fetch(downloadUrl, {
        method: 'GET',
      });

      console.log("📡 Response Status:", response.status);
      console.log("📡 Response Headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Download HTTP Error Response:", errorText);
        throw new Error(`Failed to download invoice: ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      console.log("✅ Invoice blob received:", {
        size: blob.size,
        type: blob.type
      });

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const filename = `Triva_Invoice_${paymentDetails.invoiceNumber || paymentDetails.userPaymentId}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.download = filename;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);

      console.log("✅ Invoice downloaded successfully:", filename);
      showToast('success', 'Invoice downloaded successfully!');
      console.log("=== DOWNLOAD COMPLETE ===");

    } catch (error) {
      console.error("=== DOWNLOAD INVOICE ERROR ===");
      console.error("❌ Error:", error);
      console.error("=== DOWNLOAD INVOICE ERROR END ===");
      
      const errorMsg = error instanceof Error
          ? `Failed to download invoice: ${error.message}`
          : "Failed to download invoice";
      setError(errorMsg);
      showToast('error', errorMsg);
    } finally {
      setIsDownloading(false);
    }
  };

  // Enhanced invoice handling - Send email with backend invoice
  const handleGetInvoice = async () => {
    const userId = getUserId();

    if (!userId) {
      const errorMsg = "User ID not found. Please log in again.";
      setError(errorMsg);
      showToast('error', errorMsg);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Step 1: Call GET_USER_INFO API to check if email exists
      console.log("Fetching user info for user ID:", userId);

      const response = await fetch(`${API_URLS.GET_USER_INFO}${userId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: GetUserInfoResponse = await response.json();
      console.log("User info response:", data);

      if (data.success !== "1") {
        throw new Error(data.message || "Failed to fetch user information");
      }

      // Check if email field is null or empty
      if (!data.user_info.email || data.user_info.email.trim() === "") {
        console.log("Email not found, showing email modal");
        // Step 2: Open popup to get email if email is null/empty
        setShowEmailModal(true);
      } else {
        console.log("Email exists:", data.user_info.email);

        // Step 3: Send email with backend invoice
        await sendInvoiceEmail(userId, data.user_info.email);
      }
    } catch (err) {
      console.error("Error fetching user info:", err);
      const errorMsg = err instanceof Error ? err.message : "Failed to fetch user information";
      setError(errorMsg);
      showToast('error', errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Send invoice email using backend API
  const sendInvoiceEmail = async (userId: number, userEmail: string) => {
    if (!paymentDetails?.userPaymentId) {
      const errorMsg = "Payment ID is missing. Unable to send invoice email.";
      setError(errorMsg);
      showToast('error', errorMsg);
      return;
    }

    setIsSendingEmail(true);

    try {
      console.log("=== SENDING INVOICE EMAIL ===");
      console.log("📋 User ID:", userId);
      console.log("📋 User Payment ID:", paymentDetails.userPaymentId);
      console.log("📧 Email:", userEmail);

      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('user_payment_id', paymentDetails.userPaymentId.toString());

      console.log("🚀 API URL:", API_URLS.UPLOAD_INVOICE_And_SEND_EMAIL_DESKTOP);
      console.log("⏰ Request Timestamp:", new Date().toISOString());

      const response = await fetch(API_URLS.UPLOAD_INVOICE_And_SEND_EMAIL_DESKTOP, {
        method: 'POST',
        body: formData
      });

      console.log("📡 Response Status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Email HTTP Error Response:", errorText);
        throw new Error(`Failed to send invoice email: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Email API Response:", result);

      if (result.success !== 1 && result.success !== "1") {
        throw new Error(result.message || 'Failed to send invoice email');
      }

      const successMessage = paymentDetails.invoiceNumber
        ? `Invoice #${paymentDetails.invoiceNumber} has been sent to ${userEmail}`
        : "Invoice has been sent to your email address";

      showToast('success', successMessage);
      console.log("=== EMAIL SENT SUCCESSFULLY ===");

      if (onGetInvoice) {
        onGetInvoice();
      }

    } catch (error) {
      console.error("=== SEND EMAIL ERROR ===");
      console.error("❌ Error:", error);
      
      const errorMsg = error instanceof Error
        ? `Failed to send invoice: ${error.message}`
        : "Failed to send invoice";
      setError(errorMsg);
      showToast('error', errorMsg);
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Enhanced email submit with invoice sending
  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError("Please enter a valid email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    const userId = getUserId();
    if (!userId) {
      const errorMsg = "User ID not found. Please log in again.";
      setError(errorMsg);
      showToast('error', errorMsg);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Get current user info first to preserve existing data
      const getUserResponse = await fetch(`${API_URLS.GET_USER_INFO}${userId}`);
      if (!getUserResponse.ok) {
        throw new Error("Failed to fetch current user information");
      }

      const userData: GetUserInfoResponse = await getUserResponse.json();
      if (userData.success !== "1") {
        throw new Error("Failed to fetch current user information");
      }

      // Prepare form data for UPDATE_USER_INFO API
      const formData = new FormData();
      formData.append("user_id", userId.toString());
      formData.append("user_fname", userData.user_info.user_fname || "");
      formData.append("user_lname", userData.user_info.user_lname || "");
      formData.append("user_mobileno", userData.user_info.user_mobileno || "");
      formData.append("email", email.trim());
      // Don't append profile if it's empty or just a path
      if (userData.user_info.profile && userData.user_info.profile !== "") {
        formData.append("profile", userData.user_info.profile);
      }

      console.log("Updating user info with email:", email);

      const updateResponse = await fetch(API_URLS.UPDATE_USER_INFO, {
        method: "POST",
        body: formData,
      });

      if (!updateResponse.ok) {
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      }

      const updateData: UpdateUserInfoResponse = await updateResponse.json();
      console.log("Update user info response:", updateData);

      if (updateData.success !== "1") {
        throw new Error(updateData.message || "Failed to update email");
      }

      // Success! Close modal
      setShowEmailModal(false);
      setEmail("");
      showToast('success', 'Email updated successfully!');

      // Send invoice email
      await sendInvoiceEmail(userId, email.trim());
      
    } catch (err) {
      console.error("Error updating user info:", err);
      const errorMsg = err instanceof Error ? err.message : "Failed to update email";
      setError(errorMsg);
      showToast('error', errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    setShowEmailModal(false);
    setEmail("");
    setError("");
  };

  const handleCloseModal = () => {
    setShowEmailModal(false);
    setEmail("");
    setError("");
  };

  const handleBackToPlans = () => {
    // Clear stored payment info when going back to plans
    localStorage.removeItem("lastPaymentSuccess");

    if (onBackToPlans) {
      onBackToPlans();
    } else {
      window.location.href = "https://www.mytriva.com";
    }
  };

  return (
    <>
      <div className="min-h-screen flex">
        {/* Left Section - Success Message */}
        <div className="w-1/2 bg-white px-12">
          {/* Triva Logo */}
          <div className="w-full px-12 py-3 mt-4 3xl:mt-10 4xl:mt-16">
            <div className="flex items-center justify-between">
              <div className="w-[120px] h-[36px] 2xl:w-[120px] 3xl:w-[172px] 3xl:h-[48px] 4xl:w-[200px] 4xl:h-[73px] cursor-pointer">
                <Logo className="w-full h-full object-contain" />
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center min-h-[calc(100vh-200px)]">
            {/* Success Icon */}
            <div className="mb-8">
              <div className="relative flex items-center justify-center mx-auto -mb-10 w-[280px] h-[280px]">
                {/* Background GIF Animation */}
                <img
                  src={PartyConfetti}
                  alt="Party Confetti Animation"
                  className="absolute inset-0 w-full h-full object-contain z-0"
                />

                {/* Gift Box Icon aligned left-center */}
                <div className="relative z-10 flex items-center justify-center h-full w-full">
                  <GiftBox className="w-[140px] h-[140px]" />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="mb-5 text-center max-w-md 3xl:max-w-lg">
              <div className="text-[20px] font-bold font-inter text-[#003E3E]">
                Thanks for subscribing
              </div>

              <div className="text-[20px] font-bold text-[#003E3E] font-inter -mt-1">
                {getPlanDisplayName()}
              </div>

              {/* Icon directly below plan name */}
              <div className="flex justify-center -mt-2 mb-4">
                <NameBorderIcon />
              </div>

              <div className="text-[12px] 3xl:text-[16px] text-[#003E3E] font-inter">
                Your plan is being prepared and will be shared with you within
                <span className="font-semibold"> 24 hours (or sooner).</span>
                Thank you for your patience!
              </div>
            </div>

            <div className="mb-6 max-w-md w-full">
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                <table className="w-full">
                  <tbody className="">
                    <tr className="bg-[#F1FBFB] border-b border-gray-100">
                      <td className="px-4 py-3 text-base font-bold text-[#003E3E]">
                        Invoice No.
                      </td>
                      <td className="px-4 py-3 text-base font-bold text-[#003E3E] text-right">
                        #{paymentDetails?.invoiceNumber || "N/A"}
                      </td>
                    </tr>
                    <tr className="">
                      <td className="px-4 py-2 text-sm font-medium text-gray-700">
                        Plan name
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 font-semibold text-right">
                        {getPlanDisplayName()}
                      </td>
                    </tr>
                    <tr className="">
                      <td className="px-4 py-2 text-sm font-medium text-gray-700">
                        Duration
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 font-semibold text-right">
                        {formatDuration(duration || 0)}
                      </td>
                    </tr>
                    <tr className="">
                      <td className="px-4 py-2 text-sm font-medium text-gray-700">
                        Price
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 font-semibold text-right">
                        ₹{" "}
                        {paymentDetails
                          ? new Intl.NumberFormat("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(paymentDetails.amount / 1.18)
                          : "0.00"}
                      </td>
                    </tr>
                    <tr className="">
                      <td className="px-4 py-2 text-sm font-medium text-gray-700">
                        TAX (18%)
                      </td>
                      <td className="px-4 py-2 text-sm text-[#0DB5B5] font-semibold text-right">
                        ₹{" "}
                        {paymentDetails
                          ? new Intl.NumberFormat("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(
                              paymentDetails.amount -
                                paymentDetails.amount / 1.18
                            )
                          : "0.00"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Error Message */}
            {/* {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg max-w-md w-full">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )} */}

            {/* Action Buttons */}
            <div className="flex gap-2 justify-center px-6">
              {/* Download Invoice Button */}
              <button
                onClick={handleDownloadInvoice}
                disabled={isDownloading || !paymentDetails?.userPaymentId}
                className="bg-[#0DB5B5] w-full text-white px-4 py-3 rounded-lg font-medium hover:bg-[#0A9999] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                {isDownloading ? "Downloading..." : "Download invoice"}
              </button>
            </div>
          </div>
        </div>

        {/* Right Section - Install App */}
        <div className="w-1/2 bg-[#F1FBFB] flex items-center justify-center relative overflow-hidden">
          {/* Install App Section */}
          <div className="flex-1 relative min-h-[600px] flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center">
              <SuccessIcon className="w-[540px] h-[600px] 2xl:h-[600px] 3xl:w-[600px] 3xl:h-[700px] 4xl:w-[700px] 4xl:h-[800px]" />
            </div>
          </div>

          {/* App Store Badges */}
          <div className="fixed bottom-6 w-[200px] h-[60px] 2xl:w-[200px] 2xl:h-[60px] 3xl:w-[250px] 3xl:h-[80px] 4xl:w-[350px] 4xl:h-[100px] cursor-pointer">
            <div className="absolute inset-0 flex items-center justify-center">
              <AvailableOn className="w-full h-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 relative">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              disabled={isLoading || isSendingEmail}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Content */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-[#202020] mb-2">
                Update email
              </h2>
              <p className="text-[#646464] text-sm">
                Enter your email address{" "}
                <span className="text-[#999999]">(optional)</span>
              </p>
            </div>

            {/* Email Input */}
            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(""); // Clear error when user types
                }}
                placeholder="example@gmail.com"
                disabled={isLoading || isSendingEmail}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0DB5B5] focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            {/* Info Message */}
            <div className="mb-6 flex items-start">
              <Info className="w-4 h-4 text-[#999999] mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-[#999999] text-xs">
                Want your invoice details sent to your inbox? Just enter your
                email.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Loading/Sending Status */}
            {(isLoading || isSendingEmail) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  <p className="text-blue-800 text-sm">
                    {isSendingEmail
                      ? "Sending invoice..."
                      : "Processing..."}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                disabled={isLoading || isSendingEmail}
                className="flex-1 px-4 py-3 border border-[#0DB5B5] text-[#0DB5B5] rounded-lg font-medium hover:bg-[#0DB5B5] hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSendingEmail ? "Please wait..." : "Skip"}
              </button>
              <button
                onClick={handleEmailSubmit}
                disabled={
                  isLoading || isSendingEmail || !isValidEmail(email)
                }
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isValidEmail(email) && !isLoading && !isSendingEmail
                    ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                    : "bg-[#CCCCCC] text-white"
                }`}
              >
                {isLoading
                  ? "Submitting..."
                  : isSendingEmail
                  ? "Sending..."
                  : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentSuccessPageDesktop;